// @ts-nocheck
// Ported byte-identically from obd3-gui v1
// (src/features/dashboard/components/WidgetCard.tsx).
// A dashboard tile: rounded border card with an optional chrome header
// (drag handle, icon, title, size cycler, remove ✕). `compact` hides the
// chrome in display mode so KPI tiles show only their body content —
// the header only appears while the user is in Customize mode.
import type { LucideIcon } from 'lucide-react';
import { GripVertical, Maximize2, X } from 'lucide-react';
import type {
  DraggableAttributes,
  DraggableSyntheticListeners,
} from '@dnd-kit/core';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils/cn';
import { SIZE_DEFS, SIZE_ORDER, type WidgetSize } from '../config';

interface WidgetCardProps {
  title: string;
  icon: LucideIcon;
  editing?: boolean;
  onRemove?: () => void;
  description?: string;
  children?: React.ReactNode;
  className?: string;
  dragAttributes?: DraggableAttributes;
  dragListeners?: DraggableSyntheticListeners;
  size?: WidgetSize;
  onSizeChange?: (size: WidgetSize) => void;
  compact?: boolean;
}

export function WidgetCard({
  title,
  icon: Icon,
  editing,
  onRemove,
  description,
  children,
  className,
  dragAttributes,
  dragListeners,
  size,
  onSizeChange,
  compact = false,
}: WidgetCardProps) {
  const showHeader = editing || !compact;
  return (
    <div
      className={cn(
        'group/card shadow-neu-raised flex h-full min-h-0 flex-col overflow-hidden rounded-2xl bg-card',
        // Softer hover — a whisper of lift + deeper shadow, no scale. Scale was
        // causing edges to look fuzzy on dense grids and made the whole board
        // feel restless when the pointer moved through it. Disabled while
        // editing so the dnd-kit transform stays predictable.
        'transition-[transform,box-shadow] duration-200 ease-out',
        !editing && 'hover:-translate-y-[1px]',
        editing && 'ring-2 ring-primary/40',
        className,
      )}
    >
      {showHeader && (
        <div className="flex items-start gap-2 border-b border-border/60 bg-gradient-to-b from-muted/40 to-muted/10 px-3 py-2">
          {editing && (
            <button
              type="button"
              {...dragAttributes}
              {...(dragListeners as React.HTMLAttributes<HTMLButtonElement>)}
              className="mt-0.5 flex h-5 w-5 cursor-grab items-center justify-center rounded text-muted-foreground hover:bg-accent/60 active:cursor-grabbing"
              aria-label="Drag handle"
            >
              <GripVertical className="h-3.5 w-3.5" />
            </button>
          )}
          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
            <Icon className="h-3 w-3" strokeWidth={2.4} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="truncate text-[11px] font-semibold uppercase tracking-[0.09em] text-foreground/90">
              {title}
            </div>
            {description && (
              <div className="truncate text-[10px] text-muted-foreground">
                {description}
              </div>
            )}
          </div>
          {editing && size && onSizeChange && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  aria-label="Resize"
                  title={`Size: ${SIZE_DEFS[size].label}`}
                >
                  <Maximize2 className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-32">
                <DropdownMenuLabel>Size</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {SIZE_ORDER.map((s) => (
                  <DropdownMenuItem
                    key={s}
                    onClick={() => onSizeChange(s)}
                    className={cn(
                      s === size && 'bg-primary/10 font-semibold text-primary',
                    )}
                  >
                    {SIZE_DEFS[s].label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {editing && onRemove && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-destructive hover:text-destructive"
              onClick={onRemove}
              aria-label="Remove widget"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      )}
      <div className={cn('min-h-0 flex-1 overflow-auto', compact ? 'p-2.5' : 'p-3')}>
        {children}
      </div>
    </div>
  );
}
